import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Button, Form, Input } from 'antd';
import axiosInstance from '../utils/axiosInstance';
import CustomSelect from '../Components/CustomSelect';
import { useTranslation } from 'react-i18next';
import AppShell from '../Components/AppShell';

const Language_option = {
    "en": "English",
    "zh": "中文",
    "ms": "Malay"
}

const RegisterPage: React.FC = () => {
    const [selectedLanguage, setSelectedLanguage] = useState('English');
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [buttonDisable, setButtonDisable] = useState(false);

    useEffect(() => {
        setSelectedLanguage(Language_option[i18n.language as keyof typeof Language_option])
    }, [i18n?.language])

    const register = async (username: string, password: string, phoneNo: string, withdrawalPassword: string, referralCode: string) => {
        try {
            const response = await axiosInstance.post("/auth/register", {
                referralCode,
                username,
                phoneNo,
                password,
                withdrawalPassword
            })
            if (response?.status === 201) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Registered successfully',
                    icon: 'success',
                }).then(e => {
                    navigate('/login');
                })

            } else {
                Swal.fire({
                    title: 'Error!',
                    text: 'Register failed',
                    icon: 'error',
                })
            }

        } catch (error: any) {
            console.error('Error fetching data:', error);
        }
    };

    const onFinish = async (values: any) => {
        setButtonDisable(true);
        console.log('Received values of form: ', values);
        // Ensure phoneNo starts with '0'
        let phoneNo = (values.phoneNo || '').trim();
        if (!phoneNo.startsWith('0')) {
            phoneNo = '0' + phoneNo;
        }
        await register(values.username, values.password, phoneNo, values.withdrawalPassword, "");
        setButtonDisable(false);
    };

    useEffect(() => {
    }, [])

    return (
        <AppShell backdrop="/images/BG-019.png" backdropOpacity={1} chrome={false}>
            <div className="lg:grid lg:grid-cols-[55fr_45fr] lg:min-h-[100dvh]">
                {/* Desktop-only imagery column */}
                <div
                    className="hidden lg:block bg-center bg-cover"
                    style={{ backgroundImage: "url('/images/bonia/bonia-front-store.webp')" }}
                    aria-hidden="true"
                />
                {/* Form column — existing markup, unchanged */}
                <div className="lg:flex lg:flex-col lg:justify-center lg:px-20">
                    <div className='p-4 text-black'>
                        <div className='mt-6 text-white text-[20px]' onClick={() => navigate("/login")}>{"<"} Back </div>
                        <div>
                            {/* The Bonia lockup ships only in black; `invert` turns it white
                                for this page's dark backdrop, so there is one logo asset. */}
                            <img src="/images/bonia/Bonia-Logo-01.png" className='w-[25%] mt-3 invert' alt="Bonia" />
                        </div>
                    </div>
                    <div className='mx-4 flex flex-col gap-2 bg-brand-50 rounded-md p-4'>
                        <div className='flex justify-between'>
                            <div className="text-2xl font-bold">{t("register_now")}</div>
                            <div className='w-[150px] font-bold'>
                                <CustomSelect value={selectedLanguage} options={["English", "中文", "Malay"]}
                                    onChange={(e) => {
                                        const entry = Object.entries(Language_option).find(([_, val]) => val === e);
                                        i18n.changeLanguage(entry?.[0] ?? "en")
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            {t("please_register_to_continue")}
                        </div>

                        <Form
                            name="login"
                            initialValues={{ remember: true }}
                            className='w-[98%] m-auto'
                            onFinish={onFinish}
                        >
                            <Form.Item
                                name="phoneNo"
                                rules={[{ required: true, message: '' }]}
                            >
                                <div className='flex bg-white rounded-md border-2 border-solid text-[16px] items-center'>
                                    <p className='px-[11px] py-[4px] min-w-fit'>{t("phoneNo")}</p>
                                    <span className="pl-1">60</span>
                                    <Input className='text-right' size="large" placeholder={t("type_here")} variant="borderless" />
                                </div>
                            </Form.Item>
                            <Form.Item
                                name="username"
                                rules={[{ required: true, message: '' }]}
                            >
                                <div className='flex bg-white rounded-md border-2 border-solid text-[16px]'>
                                    <p className='px-[11px] py-[4px] min-w-fit'>{t("username")}</p>
                                    <Input className='text-right' size="large" placeholder={t("type_here")} variant="borderless" />
                                </div>
                            </Form.Item>
                            <Form.Item
                                name="password"
                                rules={[{ required: true, message: '' }]}
                            >
                                <div className='flex bg-white rounded-md border-2 border-solid text-[16px]'>
                                    <p className='px-[11px] py-[4px] min-w-fit'>{t("password")}</p>
                                    <Input className='text-right' type="password" size="large" placeholder={t("type_here")} variant="borderless" />
                                </div>
                            </Form.Item>
                            <Form.Item
                                name="withdrawalPassword"
                                rules={[{ required: true, message: '' }]}
                            >
                                <div className='flex bg-white rounded-md border-2 border-solid text-[16px]'>
                                    <p className='px-[11px] py-[4px] min-w-fit'>{t("withdrawal_password")}</p>
                                    <Input className='text-right' type="password" size="large" placeholder={t("type_here")} variant="borderless" />
                                </div>
                            </Form.Item>


                            <Form.Item>
                                <Button
                                    className='w-full text-white bg-brand-800'
                                    size='large' type="primary" htmlType="submit"
                                    disabled={buttonDisable}>
                                    {t("register_now")}
                                </Button>
                            </Form.Item>
                            <div className='text-center text-brand-800 text-[16px]'
                                onClick={() => navigate("/login")}>{t("login_now")}</div>
                        </Form>

                        <div className='text-xs text-center'>Copyright © 2025 Bonia. All Rights Reserved.</div>

                    </div>
                </div>
            </div>
        </AppShell>
    );
};

export default RegisterPage;
